import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../api/api";

const WithdrawPage = () => {
  const [amount, setAmount] = useState("");
  const [bankCode, setBankCode] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [banks, setBanks] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [withdrawalHistory, setWithdrawalHistory] = useState(null);
  const [orders, setOrders] = useState([]);
  const [selectedOrderId, setSelectedOrderId] = useState("");

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const navigate = useNavigate();

  // LOAD BANKS, WALLET, ORDERS, WITHDRAWAL HISTORY
  useEffect(() => {
    const loadData = async () => {
      try {
        const bankRes = await api.bank.list();
        if (bankRes.status) setBanks(bankRes.banks);

        const whistoryRes = await api.withdrawal.listMine();
        let withdrawalHistoryList = [];
        if (whistoryRes.status) {
          setWithdrawalHistory(whistoryRes);
          withdrawalHistoryList = whistoryRes.withdrawals || [];
        }

        const walletRes = await api.withdrawal.balance();
        if (walletRes.status) setWallet(walletRes.wallet);

        const orderRes = await api.orders.vendorMine();
        if (orderRes && Array.isArray(orderRes.orders)) {
          const paidOrders = orderRes.orders.filter(
            (o) => o.paymentStatus === "paid"
          );

          // REMOVE ORDERS THAT ALREADY HAVE SUCCESSFUL WITHDRAWAL
          const successfulOrderIds = withdrawalHistoryList
            .filter((w) => w.status === "success")
            .map((w) => w.orderId);

          const filteredOrders = paidOrders.filter(
            (o) => !successfulOrderIds.includes(o._id)
          );

          setOrders(filteredOrders);
        }
      } catch (err) {
        console.log("Error loading:", err);
      }
    };
    loadData();
  }, []);

  // SUBMIT WITHDRAWAL
  const handleWithdraw = async () => {
    if (!selectedOrderId)
      return setMessage("Select an order to withdraw from");

    const order = orders.find((o) => o._id === selectedOrderId);
    if (!order) return setMessage("Invalid order selected");

    if (order.status !== "delivered")
      return setMessage("Order must be delivered before withdrawing.");

    if (!amount || Number(amount) <= 0)
      return setMessage("Enter a valid amount");

    if (!bankCode)
      return setMessage("Select a bank");

    if (!accountNumber || accountNumber.length < 10)
      return setMessage("Enter a valid 10-digit account number");

    setLoading(true);
    setMessage("");

    try {
      const res = await api.withdrawal.create({
        amount: Number(amount),
        bankName: bankCode,
        accountNumber,
        orderId: selectedOrderId,
      });

      if (res.status) {
        setMessage("Withdrawal request submitted successfully!");
        setAmount("");
        setBankCode("");
        setAccountNumber("");
        setSelectedOrderId("");
      } else {
        setMessage(res.message || "Unable to withdraw.");
      }
    } catch (err) {
      console.log("Error: ", err);
      setMessage("Error connecting to server.");
    } finally {
      setLoading(false);
    }
  };

  const selectedOrder = orders.find((o) => o._id === selectedOrderId);

  return (
    <div className="min-h-screen bg-[#fff8e1] flex flex-col items-center pt-16 px-6 relative">

      {/* BACK BUTTON */}
      <button
        onClick={() => navigate(-1)}
        className="absolute left-4 top-4 text-[#5d4037] text-2xl"
      >
        ←
      </button>

      <h2 className="text-2xl font-bold text-[#5d4037]">Withdraw Earnings</h2>

      {/* WALLET BALANCE */}
      <div className="mt-4 text-lg font-semibold text-[#5d4037] bg-white shadow p-3 rounded-lg">
        Balance: ₦{wallet ? (wallet.balance / 100).toLocaleString() : "0.00"}
      </div>

      {/* ORDER SELECTION */}
      <div className="w-full max-w-md mt-6">
        <label className="text-[#5d4037] font-semibold">Select Order</label>

        <select
          className="w-full mt-2 p-3 border rounded-md"
          value={selectedOrderId}
          onChange={(e) => setSelectedOrderId(e.target.value)}
        >
          <option value="">Choose Order</option>

          {orders.map((order) => {
            const firstItem = order.items[0];
            const name = firstItem?.productId?.name || "Order";
            const total = order.totalAmount;
            return (
              <option key={order._id} value={order._id}>
                {name} — ₦{total.toLocaleString()} ({order.paymentStatus})
              </option>
            );
          })}
        </select>

        {/* ORDER PREVIEW CARD */}
        {selectedOrder && (
          <div className="mt-4 bg-white shadow rounded-lg p-4 flex gap-3">
            <img
              src={
                selectedOrder.items[0]?.productId?.images?.[0] ||
                "https://via.placeholder.com/80"
              }
              alt="order"
              className="w-16 h-16 rounded-lg object-cover"
            />

            <div>
              <p className="font-bold text-[#5d4037]">
                {selectedOrder.items[0]?.productId?.name}
              </p>

              <p className="text-sm">
                Amount: ₦{selectedOrder.totalAmount?.toLocaleString()}
              </p>

              <p className="text-sm">
                Payment: {selectedOrder.paymentStatus}
              </p>

              <p
                className="text-sm"
                style={{
                  color: selectedOrder.status === "delivered" ? "green" : "red",
                }}
              >
                Status: {selectedOrder.status}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* AMOUNT INPUT */}
      <input
        type="number"
        placeholder="Enter amount"
        className="mt-6 w-full max-w-md p-3 border rounded-md"
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
      />

      {/* BANK LIST */}
      <select
        className="mt-4 w-full max-w-md p-3 border rounded-md"
        value={bankCode}
        onChange={(e) => setBankCode(e.target.value)}
      >
        <option value="">Select Bank</option>
        {banks.map((b) => (
          <option key={b.code} value={b.code}>
            {b.name}
          </option>
        ))}
      </select>

      {/* ACCOUNT NUMBER */}
      <input
        type="number"
        placeholder="Account Number"
        className="mt-4 w-full max-w-md p-3 border rounded-md"
        value={accountNumber}
        onChange={(e) => setAccountNumber(e.target.value)}
      />

      {/* SUBMIT BUTTON */}
      <button
        onClick={handleWithdraw}
        disabled={loading}
        className="mt-6 bg-[#c2452d] w-full max-w-md text-white px-6 py-3 rounded-lg disabled:opacity-60"
      >
        {loading ? "Processing..." : "Withdraw Now"}
      </button>

      {/* MESSAGE */}
      {message && (
        <p className="mt-4 text-center text-[#5d4037] font-semibold">
          {message}
        </p>
      )}

      {/* WITHDRAWAL HISTORY SECTION */}
      {withdrawalHistory && withdrawalHistory.withdrawals?.length > 0 && (
        <div className="w-full max-w-md mt-10 pb-24">
          <h3 className="text-xl font-bold text-[#5d4037] mb-3">
            Withdrawal History
          </h3>

          <div className="space-y-4 max-h-80 overflow-y-auto pr-1">
            {withdrawalHistory.withdrawals.map((w) => {
              const statusColor =
                w.status === "success"
                  ? "text-green-600 bg-green-100"
                  : w.status === "pending"
                  ? "text-yellow-700 bg-yellow-100"
                  : "text-red-700 bg-red-100";

              return (
                <div
                  key={w._id}
                  className="bg-white shadow rounded-lg p-4 border border-[#e0d7c6]"
                >
                  <div className="flex justify-between items-center">
                    <p className="font-semibold text-[#5d4037]">
                      ₦{(w.amount / 100).toLocaleString()}
                    </p>

                    <span
                      className={`px-3 py-1 rounded-full text-sm font-semibold ${statusColor}`}
                    >
                      {w.status}
                    </span>
                  </div>

                  {w.orderId && (
                    <p className="text-sm mt-1 text-[#5d4037]">
                      Order: {w.orderId}
                    </p>
                  )}

                  <p className="text-xs text-gray-500 mt-2">
                    {new Date(w.createdAt).toLocaleString()}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default WithdrawPage;
